import React, {
  useCallback,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation, useRouteMatch, useHistory } from "react-router-dom";

import { useMemo } from "react";
import { STORE_INFO_MAPPING, STORE_INFO_USA } from "./mapping";

import { Cookies } from "react-cookie";

import { allRoutes } from "./routes";
import { LANGUAGE_PATH, useMatchNewDesignRoute } from "hooks/useIsNewDesign";
import {
  CountryCode,
  StoreId,
  Language,
  Nullish,
} from "@ubnt/store.core/lib/types";
import { REGIONAL_MICROSITE_PREFIX_PATH } from "hooks/useIsNewDesign";
import {
  evalCountryCode,
  evalStoreId,
  evalLanguageCode,
  saveCookie,
} from "./utils";

import {
  pathnameToParts,
  partsToPathname,
} from "@ubnt/store.core/lib/utils/url";
import { useEffectOnce } from "react-use";
import {
  DEFAULT_COUNTRY_CODE,
  DEFAULT_LANGUAGE,
  GLOBAL_SITE_CONFIG,
  UI_COM_SITE_CONFIG,
} from "./constants";
import { SiteSettingsContextValue } from "./types";

/**
 * Changed from "country-code" to "country-code-session" to solve issue that was caused
 * with microsite language cookie PR where the country code was set to expire in 1 year
 * instead of session.
 */
export const COOKIE_COUNTRY_CODE = "country-code-session";
export const COOKIE_MICROSITE_LANGUAGE = "microsite-language";

const cookies = new Cookies();

export const LocalStorageKey = {
  StoreReferer: "store-referer",
  LanguageReferer: "language-referer",
};

export const ModalId = {
  Support: "support",
  Account: "account",
  MobileMenu: "mobileMenu",
};

export const SiteSettings = createContext<SiteSettingsContextValue | null>(
  null,
);

export const SiteSettingsProvider = ({ children }) => {
  const location = useLocation();
  const history = useHistory();

  // #############################################################################
  // Geo-IP detection
  // #############################################################################
  /**
   * null - Not processed
   * undefined - Geo-IP detection failed
   * CountryCode - Geo-IP detection succeeded
   */
  const [geoCountryCode, setGeoCountryCode] =
    useState<Nullish<CountryCode>>(null);

  useEffect(() => {
    // Cookies
    const cookieCountryCode = evalCountryCode(cookies.get(COOKIE_COUNTRY_CODE));

    if (cookieCountryCode) {
      setGeoCountryCode(cookieCountryCode);

      // Exit early if we already have stored value
      return;
    }

    let controller: AbortController | null = null;
    const runAsync = async () => {
      try {
        controller = new AbortController();

        const url = new URL(GLOBAL_SITE_CONFIG.store.defaultRegion.baseUrl);
        url.pathname = "/api/geo-ip";

        const res = await fetch(url.toString(), {
          mode: "cors",
          signal: controller.signal,
          // Semi new option (not supported on all browsers) - https://developer.mozilla.org/en-US/docs/Web/API/fetch#browser_compatibility
          priority: "high",
        });
        const geoResult = await res.json();

        // const geoStoreId = geoResult?.preferredStoreId;
        const geoCountry = geoResult?.country;

        const detectedCountryCode = evalCountryCode(geoCountry);

        cookies.set(COOKIE_COUNTRY_CODE, detectedCountryCode, {
          path: "/",
          domain: ".ui.com",
          expires: undefined,
          secure: true,
          sameSite: "lax",
        });

        setGeoCountryCode(detectedCountryCode);
      } catch (err) {
        // Silence errors
        console.error("Failed to run geo-location", err);
      }
    };

    runAsync();

    return () => {
      if (controller) {
        controller.abort();
      }
    };
  }, []);

  // #############################################################################
  // Country code
  // #############################################################################
  const countryCode = useMemo(() => {
    /**
     * If geoCountryCode is null, it means that the geo-ip detection is not yet
     * processed. If it is undefined, it means that the geo-ip detection failed.
     * In both cases, we want to fallback to the default country code.
     */
    return geoCountryCode ?? DEFAULT_COUNTRY_CODE;
  }, [geoCountryCode]);

  // #############################################################################
  // Store and language referer
  // #############################################################################
  /**
   * undefined - Not processed
   * null - Not found
   * StoreId - Found store
   */
  const [refererStoreId, setRefererStoreId] =
    useState<Nullish<StoreId>>(undefined);
  const [refererLanguage, setRefererLanguage] =
    useState<Nullish<Language>>(undefined);

  // Get the url/stored value
  useEffect(() => {
    // Handle URL value
    const urlParams = new URLSearchParams(location.search);

    // Skip if no store param
    if (urlParams.has("s") || urlParams.has("l")) {
      // Read and parse the search param
      const urlStoreId = urlParams.get("s")?.toLowerCase();
      const urlLanguage = urlParams.get("l")?.toLowerCase();

      const evaluatedStoreId = evalStoreId(urlStoreId);
      const evaluatedLanguage = evalLanguageCode(urlLanguage);

      if (evaluatedStoreId) {
        setRefererStoreId(evaluatedStoreId);

        // Add the value to local storage
        localStorage.setItem(LocalStorageKey.StoreReferer, evaluatedStoreId);
      }

      if (
        (evaluatedLanguage &&
          evaluatedStoreId &&
          UI_COM_SITE_CONFIG.regions[evaluatedStoreId]?.languages?.includes(
            evaluatedLanguage,
          )) ||
        (!evaluatedStoreId &&
          UI_COM_SITE_CONFIG.defaultRegion.languages?.includes(
            evaluatedLanguage,
          ))
      ) {
        setRefererLanguage(evaluatedLanguage);

        // Add the value to local storage
        localStorage.setItem(
          LocalStorageKey.LanguageReferer,
          evaluatedLanguage,
        );

        // Override preference cookie as well
        saveCookie(COOKIE_MICROSITE_LANGUAGE, evaluatedLanguage.toUpperCase());
      }

      // Remove the search param
      urlParams.delete("s");
      urlParams.delete("l");

      // Replace the search params
      setTimeout(() => {
        const searchString = urlParams.toString();
        const searchStringFragment = searchString
          ? `?${urlParams.toString()}`
          : "";
        const hashFragment = location.hash;
        const newUrl = [
          location.pathname,
          searchStringFragment,
          hashFragment,
        ].join("");

        history.replace(newUrl);
      }, 0);

      return;
    }

    const storageStoreId = localStorage.getItem(LocalStorageKey.StoreReferer);
    const storageLanguage = localStorage.getItem(
      LocalStorageKey.LanguageReferer,
    );

    const evaluatedStoreId = evalStoreId(storageStoreId);
    const evaluatedLanguage = evalLanguageCode(storageLanguage);

    // Handle local storage value
    if (evaluatedStoreId) {
      setRefererStoreId(evaluatedStoreId);
    } else {
      // Not found
      setRefererStoreId(null);
    }

    if (storageLanguage) {
      setRefererLanguage(evaluatedLanguage);
      saveCookie(COOKIE_MICROSITE_LANGUAGE, evaluatedLanguage.toUpperCase());
    } else {
      // Not found
      setRefererLanguage(null);
    }
  }, [history, location.pathname, location.search, location.hash]);

  const newDesignRoute = useMatchNewDesignRoute();

  // #############################################################################
  // Parse final store
  // #############################################################################
  const store = useMemo(() => {
    // 1. Route matched
    const routeStoreId = newDesignRoute?.params?.storeId ?? StoreId.Us;
    const routeStore =
      routeStoreId && STORE_INFO_MAPPING.find((el) => el.id === routeStoreId);

    if (routeStore) {
      return routeStore;
    }

    // 2. By referer store
    const refererStore =
      refererStoreId &&
      STORE_INFO_MAPPING.find((el) => el.id === refererStoreId);
    if (refererStore) {
      return refererStore;
    }

    // 3. Get geo country store
    const geoStore =
      countryCode &&
      STORE_INFO_MAPPING.find((el) => el.countries.includes(countryCode));

    if (geoStore) {
      return geoStore;
    }

    // 4. Geo should always fallback to US but just in case, always return US last
    return STORE_INFO_USA;
  }, [countryCode, refererStoreId, newDesignRoute]);

  // #############################################################################
  // Language cookie
  // #############################################################################
  const languageCookie = useMemo(() => {
    const languageCookie = cookies.get(COOKIE_MICROSITE_LANGUAGE);

    // If user has not specifically set a language then do nothing
    if (!languageCookie) return;

    return evalLanguageCode(languageCookie);
  }, []);

  // #############################################################################
  // Language in URL
  // #############################################################################
  const routeMatch = useRouteMatch(
    `${REGIONAL_MICROSITE_PREFIX_PATH}${LANGUAGE_PATH}/:path*`,
  );

  const languageUrl = useMemo(() => {
    return routeMatch?.params["language"];
  }, [routeMatch]);

  // #############################################################################
  // Language
  // #############################################################################
  const language = useMemo(() => {
    return languageUrl ?? refererLanguage ?? languageCookie ?? DEFAULT_LANGUAGE;
  }, [languageCookie, languageUrl, refererLanguage]);

  /**
   * Update the language in the URL if it does not match the language in the
   * cookie.
   */
  const updateLanguageInUrl = useCallback(() => {
    /**
     * Exit early if there is no language in the URL. It is not necessary to
     * update the URL.
     */
    if (!languageUrl) return;

    /**
     * Exit early if there is no saved language in the cookie.
     */
    if (!languageCookie) return;

    /**
     * Exit early if the language in the URL matches the language in the cookie.
     */
    if (languageUrl === languageCookie) return;

    const matchedRestOfPath = routeMatch?.params["path"];

    const restOfPath = matchedRestOfPath
      ? pathnameToParts(matchedRestOfPath)
      : [];

    history.replace(partsToPathname([store.id, languageCookie, ...restOfPath]));
  }, [history, languageCookie, languageUrl, routeMatch, store.id]);

  /**
   * Run once to update the language in the URL if it does not match the language.
   */
  useEffectOnce(() => {
    updateLanguageInUrl();
  });

  // #############################################################################
  // Create full store urls
  // #############################################################################
  const routes = allRoutes(store.id, language);

  // #############################################################################
  // Routing util
  // #############################################################################
  const onLinkClick = useCallback(
    (e, destinationUrl) => {
      // Navigate to the pathname locally
      const url = new URL(destinationUrl);
      const currentUrl = new URL(window.location.href);

      const santizedHost = (hostname) =>
        hostname.toLowerCase().replace(/^www\./, "");

      if (
        santizedHost(url.hostname) === santizedHost(currentUrl.hostname) ||
        currentUrl.hostname === "localhost"
      ) {
        e.preventDefault();

        if (url.hash && url.pathname === currentUrl.pathname) {
          history.replace({ hash: url.hash });
        } else {
          history.push({ pathname: url.pathname, hash: url.hash });
        }

        if (url.hash) {
          const element = document.getElementById(url.hash.replace(/^#/, ""));
          if (element) {
            const sectionOffsetTop = element.offsetTop;

            window.scrollTo({
              top: sectionOffsetTop,
              behavior: "smooth",
            });
          }
        }
      }
    },
    [history],
  );

  /**
   * Util to get root url for default store redirect
   */
  // const getDefaultStoreLanguageRoute = useCallback(() => {
  //   const regionCode = geoCountryCode || DEFAULT_COUNTRY_CODE;

  //   // NOTE: Currently in prod we only want single language! Remove this if-statement when launching translations
  //   if (process.env.isEnvProduction) {
  //     return `/${store.id}/${DEFAULT_LANGUAGE}`;
  //   }

  //   const defaultLanguage = getDefaultLanguageByRegion(regionCode);
  //   return `/${store.id}/${defaultLanguage}`;
  // }, [geoCountryCode, store.id]);

  // #############################################################################
  // Embed
  // #############################################################################
  const isAppView = useMemo(() => {
    const urlParams = new URLSearchParams(location.search);

    return urlParams.get("appview") === "true";
  }, [location.search]);
  // #############################################################################

  // #############################################################################
  // Result
  // #############################################################################
  return (
    <SiteSettings.Provider
      value={{
        store,
        routes,
        language,
        // getDefaultStoreLanguageRoute,
        geoCountryCode,
        isAppView,
        onLinkClick,
      }}
    >
      {children}
    </SiteSettings.Provider>
  );
};

export function useSiteSettings() {
  const context = useContext(SiteSettings);
  if (context === null) {
    throw new Error("useSiteSettings must be used within a SiteSettings");
  }
  return context;
}
